import { useMetrics } from '@/composables/useMetrics'
import { useUserStore } from '@/shared/stores/userStore'
import {
  AIChatSessionTypeEnum,
  EventNameEnum,
  UsersService,
  type AIChatSession,
  type CustomerUser
} from '@/types/generated'
import { ref, watch, type Ref } from 'vue'

/**
 * One-time Verdicts onboarding bubble shown in the chat thread when the user
 * enables the verdict toggle for the first time ever.
 *
 * The `ai_chat_verdicts_intro_seen` flag is persisted on the backend user record, so the
 * intro never reappears across sessions, reloads, toggle off/on, or cleared chats.
 *
 * Exposes:
 * - `showVerdictsIntro` — whether to render the intro bubble in the current thread.
 * - `pendingComposerPrefill` — a shared signal the composer watches to prefill a prompt
 *   when the user clicks a chip in the intro bubble.
 * - `handlePromptClick` — track + prefill when a prompt chip is clicked.
 *
 * @param courtDbEnabled - Reactive court-database toggle state from the parent component.
 * @param activeChatSession - Active chat session ref (used for metric attribution only).
 * @param sessionType - Session type of the surrounding chat (case / global / unified).
 */
export function useVerdictsIntroState(
  courtDbEnabled: Ref<boolean>,
  activeChatSession: Ref<AIChatSession | null>,
  sessionType: AIChatSessionTypeEnum
) {
  const userStore = useUserStore()
  const { trackEvent } = useMetrics()

  const showVerdictsIntro = ref(false)
  const pendingComposerPrefill = ref<string | null>(null)

  const markIntroSeenOnBackend = (user: CustomerUser) => {
    UsersService.usersPartialUpdate({
      uuid: user.uuid,
      requestBody: { ai_chat_verdicts_intro_seen: true }
    }).catch((error: unknown) => {
      console.error('Failed to mark verdicts intro as seen', error)
      user.ai_chat_verdicts_intro_seen = false
    })
  }

  watch(courtDbEnabled, (isOn, wasOn) => {
    // Hide the intro if the user turns Verdicts off — keeps the thread tidy while
    // the backend flag (persisted on first show) stops it from reappearing on re-toggle.
    if (!isOn) {
      showVerdictsIntro.value = false
      return
    }
    if (wasOn) return

    const user = userStore.user as CustomerUser | undefined
    if (!user || user.ai_chat_verdicts_intro_seen) return

    showVerdictsIntro.value = true
    // Flip the local user object synchronously so a rapid second toggle doesn't re-trigger.
    user.ai_chat_verdicts_intro_seen = true

    void trackEvent(EventNameEnum.VERDICT_INTRO_SHOWN, {
      session_uuid: activeChatSession.value?.uuid,
      session_type: sessionType
    })
    markIntroSeenOnBackend(user)
  })

  const handlePromptClick = (payload: { id: string; prompt: string }) => {
    pendingComposerPrefill.value = payload.prompt
    void trackEvent(EventNameEnum.VERDICT_INTRO_PROMPT_CLICKED, {
      prompt_id: payload.id,
      session_uuid: activeChatSession.value?.uuid,
      session_type: sessionType
    })
  }

  return {
    showVerdictsIntro,
    pendingComposerPrefill,
    handlePromptClick
  }
}
